import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { Breadcrumb, Button } from 'antd';
import { Link, useNavigate } from 'react-router';

import {
  useDeleteSchoolProgramSectionMutation,
  useGetSchoolProgramSectionsQuery,
  useReorderSchoolProgramSectionsMutation,
} from '@/api/endpoints/schools/schoolSubjectionParams/program';
import { DeleteConfirmationModal, DraggableTable, FetchDataWrapper } from '@/shared/components';
import { ROUTES, TABLE_TRANSLATIONS } from '@/shared/consts';
import { useApiRequest } from '@/shared/hooks';

import { CreateProgramSectionModal } from './components/Modals/CreateProgramSectionModal';
import { EditProgramSectionModal } from './components/Modals/EditProgramSectionModal';
import { getColumnsData } from './helpers';
import styles from './SchoolProgramSubsection.module.scss';
import type { ISectionItemData } from './types';

type TProps = {
  id: number;
  name: string;
};

export const SchoolProgramSubsection = ({ id, name }: TProps) => {
  const navigate = useNavigate();

  const {
    data: programSectionsData,
    isLoading: areProgramSectionsLoading,
    isFetching: areProgramSectionsFetching,
    isError: isProgramSectionsFetchError,
  } = useGetSchoolProgramSectionsQuery({
    schoolId: id,
  });
  const [reorderProgramSections] = useReorderSchoolProgramSectionsMutation();
  const [deleteProgramSection, { isLoading: isProgramSectionDeleting }] = useDeleteSchoolProgramSectionMutation();

  const [tableData, setTableData] = useState<ISectionItemData[]>([]);

  const [isCreateProgramSectionModalOpened, setIsCreateProgramSectionModalOpened] = useState(false);
  const [isEditProgramSectionModalOpened, setIsEditProgramSectionModalOpened] = useState(false);
  const [isDeleteProgramSectionModalOpened, setIsDeleteProgramSectionModalOpened] = useState(false);

  const [currentProgramSectionItem, setCurrentProgramSectionItem] = useState<null | ISectionItemData>(null);

  const openCreateProgramSectionModal = useCallback(() => setIsCreateProgramSectionModalOpened(true), []);
  const closeCreateProgramSectionModal = useCallback(() => setIsCreateProgramSectionModalOpened(false), []);

  const openEditProgramSectionModal = useCallback(() => setIsEditProgramSectionModalOpened(true), []);
  const closeEditProgramSectionModal = useCallback(() => setIsEditProgramSectionModalOpened(false), []);

  const openDeleteProgramSectionModal = useCallback(() => setIsDeleteProgramSectionModalOpened(true), []);
  const closeDeleteProgramSectionModal = useCallback(() => setIsDeleteProgramSectionModalOpened(false), []);

  const sendApiRequest = useApiRequest();

  const setItemToOperate = useCallback((data: ISectionItemData | null) => setCurrentProgramSectionItem(data), []);

  const handleWithClearOperateItem = (handleFunction?: VoidFunction) => {
    handleFunction?.();
    setItemToOperate(null);
  };

  const deleteProgramSectionHandle = useCallback(() => {
    if (currentProgramSectionItem) {
      sendApiRequest({
        successMessage: 'Раздел успешно удалён',
        apiRequestCallback: () =>
          deleteProgramSection({
            schoolId: id,
            programSectionId: currentProgramSectionItem?.id,
          }),
        successCallback: () => handleWithClearOperateItem(closeDeleteProgramSectionModal),
      });
    }
  }, [currentProgramSectionItem?.id, id]);

  const columns = useMemo(
    () => getColumnsData({ openEditProgramSectionModal, openDeleteProgramSectionModal, setItemToOperate }),
    [openEditProgramSectionModal, openDeleteProgramSectionModal],
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!tableData || !over) {
      return;
    }

    if (active.id !== over.id) {
      const activeIndex = tableData.findIndex((i) => i.position === active.id);
      const overIndex = tableData.findIndex((i) => i.position === over.id);

      const reorderedArray = arrayMove(tableData, activeIndex, overIndex);

      setTableData(reorderedArray);

      const reorderedArrayIds = reorderedArray.map((programSection) => programSection.id);

      sendApiRequest({
        successMessage: 'Порядок разделов программы успешно изменён',
        apiRequestCallback: () => reorderProgramSections({ schoolId: id, data: { order: reorderedArrayIds } }),
        errorCallback: () => setTableData(tableData),
      });
    }
  };

  useEffect(() => {
    setTableData(programSectionsData ?? []);
  }, [programSectionsData]);

  return (
    <FetchDataWrapper
      isLoading={areProgramSectionsLoading}
      isError={isProgramSectionsFetchError}
    >
      {programSectionsData && (
        <div className={styles.wrapper}>
          <Breadcrumb
            items={[
              { title: 'Публичные страницы' },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}`}>Школы</Link> },
              { title: <Link to={`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`}>{name}</Link> },
              { title: 'Программа' },
            ]}
          />
          <div className={styles['add-button-wrapper']}>
            <Button
              type="primary"
              onClick={openCreateProgramSectionModal}
              className={styles.button}
            >
              Добавить раздел
            </Button>
          </div>
          <DraggableTable
            onDragEnd={onDragEnd}
            keysForSorting={tableData.map((i) => i.position)}
            columns={columns}
            rowKey="position"
            size="large"
            locale={TABLE_TRANSLATIONS}
            pagination={false}
            dataSource={tableData}
            loading={areProgramSectionsFetching}
          />
          <div className={styles['action-buttons-wrapper']}>
            <Button
              onClick={() => navigate(`/${ROUTES.PUBLIC_PAGES}/${ROUTES.SCHOOLS}/${id}/${ROUTES.EDIT}`)}
              type="dashed"
              size="large"
              className={styles.button}
            >
              Назад
            </Button>
          </div>
          {isDeleteProgramSectionModalOpened && (
            <DeleteConfirmationModal
              isModalOpen={isDeleteProgramSectionModalOpened}
              description="Вы уверены, что хотите удалить раздел программы?"
              title="Удаление раздела"
              handleCancel={() => handleWithClearOperateItem(closeDeleteProgramSectionModal)}
              handleOk={deleteProgramSectionHandle}
              itemToOperate={currentProgramSectionItem}
              shouldDisableElement={isProgramSectionDeleting}
            />
          )}
          {isCreateProgramSectionModalOpened && (
            <CreateProgramSectionModal
              isModalOpen={isCreateProgramSectionModalOpened}
              handleCancel={closeCreateProgramSectionModal}
              schoolId={id}
            />
          )}
          {isEditProgramSectionModalOpened && currentProgramSectionItem && (
            <EditProgramSectionModal
              isModalOpen={isEditProgramSectionModalOpened}
              handleCancel={() => handleWithClearOperateItem(closeEditProgramSectionModal)}
              handleClearOperateItem={() => handleWithClearOperateItem()}
              itemToOperate={currentProgramSectionItem}
              schoolId={id}
            />
          )}
        </div>
      )}
    </FetchDataWrapper>
  );
};
